import { ChangeDetectionStrategy, Component, type OnInit, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MemberPickerApiService, type MemberPickerSessionContextDto } from './member-picker-api.service';
import { buildTeamTree, type TeamTreeRow } from './team-tree.util';

// org 직속(팀 없음) 멤버를 가리키는 sentinel — member-selection-resolver.ts(API)와 동일하게
// teamId===null을 "하나의 선택 가능한 노드"로 취급한다.
//
// row.id(TeamTreeRow, OrgTeamDto.id)와 confirm 엔드포인트의 MemberSelectionCriteria.teamIds는
// 이제 둘 다 "{shard}:{id}" 문자열로 값공간이 통일돼 있다(2026-08-20,
// OrgMemberDto.teamId 값공간 통일) — 그래서 그대로 전송한다. 이전엔 서버가 bare number를
// 기대해 decodeTeamTreeId()로 변환해 보냈으나, 그건 lib 추출 과정에서 한 번 빠졌다가 복구됐던
// 임시방편이었고(git show 5c038b0dd), 지금은 애초에 인코딩/디코딩 왕복 자체가 필요 없다.
const ORG_ROOT_TEAM_ID: string | null = null;

type LoadState = 'loading' | 'ready' | 'error';

// schoolbell-e 멤버 피커(2026-08-03 플랜, 2026-08-20 iframe 컨테이너 추가) — 원래
// apps/schoolbell-e/web/src/app/features/member-picker/에 있던 것을 popup·iframe 두 컨테이너가
// 공유하도록 lib으로 추출(2026-08-20-member-picker-iframe-embed.md). 이 컴포넌트는:
//   1. location.hash에서 code를 읽어 GET .../sessions/:code 한 번으로 멤버·팀·postMessage
//      대상 origin을 전부 받는다(추가 API 호출 없음).
//   2. 팀 체크박스 + 라벨 체크박스로 클라이언트 사이드에서만 선택 상태를 관리한다.
//   3. 확정 시 POST .../confirm(teamIds/labelIds) 후 opener(popup) 또는 parent(iframe)로
//      **code만** postMessage한다 — 실제 멤버 데이터(PII)는 여기서 전달되지 않는다(RP 백엔드가
//      그 code를 client_secret으로 별도 교환해야 함). popup은 postMessage 후 자기 자신을 닫고,
//      iframe은 그대로 남는다(RP 페이지가 postMessage 수신 후 처리를 직접 결정).
@Component({
  selector: 'app-member-picker',
  imports: [MatButtonModule, MatCheckboxModule, MatProgressSpinnerModule],
  templateUrl: './member-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberPickerComponent implements OnInit {
  private readonly api = inject(MemberPickerApiService);

  private code = '';

  readonly state = signal<LoadState>('loading');
  readonly errorMessage = signal<string | null>(null);
  readonly ctx = signal<MemberPickerSessionContextDto | null>(null);
  readonly confirming = signal(false);
  readonly done = signal(false);

  readonly selectedTeamIds = signal<ReadonlySet<string | null>>(new Set());
  readonly selectedLabelIds = signal<ReadonlySet<string>>(new Set());

  readonly teamRows = computed<TeamTreeRow[]>(() => buildTeamTree(this.ctx()?.teams ?? []));

  // 라벨 필터 후보 — 멤버 응답에 이미 붙어 있는 labels(멤버 레벨) + contacts[].labels(관계
  // 레벨) + contacts[].contact.labels(사람 레벨) 전체의 합집합. org-labels.controller.ts 등
  // 별도 라벨 카탈로그 조회는 이번 범위에서 필요 없다 — 멤버 응답 자체가 이미 실제로 배정된
  // 라벨만 담고 있어 그것으로 충분하다.
  readonly availableLabels = computed(() => {
    const labels = new Map<string, string>();
    for (const member of this.ctx()?.members ?? []) {
      for (const l of member.labels) labels.set(l.id, l.name);
      for (const ca of member.contacts) {
        for (const l of ca.labels) labels.set(l.id, l.name);
        for (const l of ca.contact.labels) labels.set(l.id, l.name);
      }
    }
    return [...labels.entries()].map(([id, name]) => ({ id, name }));
  });

  readonly canConfirm = computed(() => this.selectedTeamIds().size > 0 || this.selectedLabelIds().size > 0);

  readonly rootTeamId = ORG_ROOT_TEAM_ID;

  ngOnInit(): void {
    // iframe 컨테이너(schoolbell-e-embed-web)는 EmbedViewController가 만드는 부트스트랩
    // HTML이 window.__EMBED_BOOT__로 code를 넘긴다(2026-08-20-member-picker-iframe-embed.md) —
    // 서버가 frame-ancestors를 결정하는 시점(브라우저 JS 실행 전)에 code를 알아야 해서 쿼리
    // 파라미터로 받고, 그걸 그대로 전역에 심어 둔다. popup 컨테이너(schoolbell-e-web)는 이
    // 전역이 없으므로 기존 URL fragment(#code=)로 폴백한다 — 두 컨테이너가 같은 컴포넌트를
    // 쓰면서도 서로 다른 전달 경로를 그대로 지원.
    const embedBootCode = (window as { __EMBED_BOOT__?: { code?: string } }).__EMBED_BOOT__?.code;
    const hash = window.location.hash.replace(/^#/, '');
    this.code = embedBootCode || new URLSearchParams(hash).get('code') || '';
    if (!this.code) {
      this.state.set('error');
      this.errorMessage.set('잘못된 접근입니다 — 필요한 정보가 없습니다.');
      return;
    }

    this.api
      .getSessionContext(this.code)
      .then((ctx) => {
        this.ctx.set(ctx);
        this.state.set('ready');
      })
      .catch(() => {
        this.state.set('error');
        this.errorMessage.set('세션이 만료되었거나 유효하지 않습니다. 창을 닫고 다시 시도해 주세요.');
      });
  }

  toggleTeam(teamId: string | null): void {
    const next = new Set(this.selectedTeamIds());
    if (next.has(teamId)) next.delete(teamId);
    else next.add(teamId);
    this.selectedTeamIds.set(next);
  }

  toggleLabel(labelId: string): void {
    const next = new Set(this.selectedLabelIds());
    if (next.has(labelId)) next.delete(labelId);
    else next.add(labelId);
    this.selectedLabelIds.set(next);
  }

  async confirm(): Promise<void> {
    if (!this.canConfirm() || this.confirming()) return;

    this.confirming.set(true);
    this.errorMessage.set(null);

    try {
      await this.api.confirmSession(this.code, {
        // 생성 타입(Orval)은 Swagger가 "배열 원소의 null 허용"을 표현하지 못해 string[]로
        // 좁혀져 있다 — 실제 검증은 서비스 레이어(member-selection-resolver.ts)가 담당하므로
        // org 직속 sentinel(null)을 그대로 보낸다.
        teamIds: this.selectedTeamIds().size > 0 ? ([...this.selectedTeamIds()] as string[]) : undefined,
        labelIds: this.selectedLabelIds().size > 0 ? [...this.selectedLabelIds()] : undefined,
      });
      // postResultAndClose()가 false를 반환하면(대상은 있는데 origin 화이트리스트 불일치)
      // errorMessage를 이미 세팅해 뒀다 — 여기서 done을 켜면 템플릿이 done() 분기를 error()보다
      // 먼저 보여줘서 "선택이 완료됐습니다"라는 거짓 성공 화면이 뜬다(코드리뷰로 발견, 실제로는
      // RP에 아무 것도 전달 안 됨). 대상이 아예 없는 경우(standalone 접근)는 true를 반환해 기존과
      // 동일하게 완료 처리한다.
      if (this.postResultAndClose()) {
        this.done.set(true);
      }
    } catch {
      this.errorMessage.set('선택을 확정하는 중 오류가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      this.confirming.set(false);
    }
  }

  // popup(window.opener)과 iframe(window.parent) 둘 다 지원 — targetOrigin은 서버가 준
  // 화이트리스트(trustedEmbedOrigins)에서 document.referrer(popup을 연/iframe을 심은 페이지의
  // origin)와 실제로 일치하는 값만 골라 쓴다. window.opener.location/window.parent.location은
  // 크로스오리진이라 애초에 읽을 수 없고, 화이트리스트에 없는 임의 origin으로는 아예 보내지
  // 않는다(2026-08-03 플랜 §postMessage 양방향 origin 검증). 2026-08-20 iframe 컨테이너 추가
  // 전에는 window.opener가 없으면 그냥 no-op이었다 — 그래서는 iframe RP가 "사용자가 확정을
  // 끝냈다"는 신호를 exchange 폴링 없이는 받을 방법이 없었다(실제 e2e 검증 중 발견). window.
  // parent postMessage로 동일 신호를 준다. iframe은 팝업과 달리 자기 자신을 닫지 않는다 —
  // RP 페이지가 postMessage를 받은 뒤 iframe을 어떻게 할지(숨기기/제거) 직접 결정한다.
  //
  // 반환값: true면 confirm()이 done 화면을 켜도 된다(전달 성공, 또는 애초에 대상이 없어
  // standalone 접근으로 볼 수 있는 경우). false면 대상은 있는데 화이트리스트 불일치로 전달에
  // 실패한 것 — errorMessage를 세팅해 뒀으니 confirm()은 done을 켜면 안 된다(코드리뷰로 발견된
  // 거짓 성공 화면 버그, 2026-08-20).
  private postResultAndClose(): boolean {
    const target = window.opener ?? (window.parent !== window ? window.parent : null);
    if (!target) return true;
    const allowedOrigins = this.ctx()?.postMessageOrigins ?? [];
    const referrerOrigin = this.safeReferrerOrigin();
    const targetOrigin = allowedOrigins.find((o: string) => o.replace(/\/$/, '') === referrerOrigin);
    if (!targetOrigin) {
      this.errorMessage.set('결과를 전달할 수 없습니다 — 이 창을 닫고 원래 화면에서 다시 시도해 주세요.');
      return false;
    }
    target.postMessage({ code: this.code }, targetOrigin);
    if (window.opener) window.close();
    return true;
  }

  private safeReferrerOrigin(): string | null {
    if (!document.referrer) return null;
    try {
      return new URL(document.referrer).origin;
    } catch {
      return null;
    }
  }
}
